import SelectComponent from "@via/components/select/select";
import { EditProcessToolbar } from "@via/pages/projects/pipelines/workflow/components/edit-process-toolbar.tsx";
import { EditProcessGroupTypes } from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { OVERVIEW_FIELDS, USER_ROLES } from "@via/utils/constants.tsx";
import { Permissions, PermissionType } from "common";
import { useEffect, useState } from "react";
import { cleanProcessName } from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { useAbility } from "@via/context/ability-context";
import { useAuth } from "@via/hooks/useAuth";
import { classNames } from "@via/utils/helpers";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";

export default function Overview({
  draft,
  configurations,
  canEditProcess,
  handleOverviewChange,
  handleMenuGroupUpdate,
  handlePermissionUpdate,
  handleGroupIdUpdate,
  onCreateMenuGroup,
  onEditMenuGroup,
  onDeleteMenuGroup,
  permissions,
  selectedGroup,
  selectedMenuGroup,
  menuGroups,
  isMenuGroupsListLoading,
  selectedWriteUserGroups,
  handleWriteGroupIdUpdate,
  handleCreateMenuGroup,
}) {
  const { user } = useAuth();
  const [name, setName] = useState(draft?.name || "");
  const [description, setDescription] = useState(draft?.description || "");

  const ability = useAbility([
    {
      action: "Edit",
      subject: "Input",
      condition: canEditProcess,
    },
    {
      action: "Submit",
      subject: "Button",
      condition:
        user?.userRole === USER_ROLES.ADMIN
          ? true
          : canEditProcess &&
            configurations?.ENABLE_SHARE_WITH_EVERYONE_PIPELINE,
    },
  ]);

  useEffect(() => {
    setName(draft?.name || "");
    setDescription(draft?.description || "");
  }, [draft]);

  return (
    <div className="flex flex-row justify-start">
      <div className="flex w-1/2 flex-col border-r-[1px] pr-8">
        <div className="pt-3">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Name
          </label>
          <div className="mt-1">
            <input
              type="text"
              required
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="Name your Process"
              onBlur={e => {
                const value = cleanProcessName(e.target.value) as string;
                handleOverviewChange(OVERVIEW_FIELDS.NAME, value);
              }}
              disabled={!canEditProcess}
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
        </div>
        <div className="pt-3">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Description
          </label>
          <div className="mt-1">
            <textarea
              rows={3}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              disabled={!canEditProcess}
              placeholder={"Enter a Process description"}
              onBlur={e =>
                handleOverviewChange(
                  OVERVIEW_FIELDS.DESCRIPTION,
                  e.target.value
                )
              }
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div className="pt-3">
          <label className="text-sm font-medium leading-6 text-gray-900">
            Menu Group
          </label>
          <div className="flex flex-row items-center justify-between">
            <div className="w-3/4">
              <SelectComponent
                name="menuGroup"
                readOnly={!canEditProcess}
                value={selectedMenuGroup}
                enableClear={false}
                options={menuGroups}
                isLoading={isMenuGroupsListLoading}
                isCreatable
                onCreateOption={handleCreateMenuGroup}
                onChange={handleMenuGroupUpdate}
                placeholder={"Select a Menu Group"}
              />
            </div>
            <EditProcessToolbar
              type={EditProcessGroupTypes.MenuGroup}
              readOnly={!canEditProcess}
              onCreate={onCreateMenuGroup}
              onEdit={onEditMenuGroup}
              onDelete={onDeleteMenuGroup}
            />
          </div>
        </div>
        {!draft.isNewProcess && (
          <div className="pt-3">
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Revision Comment
            </label>
            <div className="mt-1">
              <textarea
                rows={3}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                disabled
                required
                placeholder={"Enter a Revision Comment"}
                defaultValue={draft?.revisionComment}
              />
            </div>
          </div>
        )}
      </div>
      <fieldset className="w-1/2 pl-8">
        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Set Permissions
        </label>
        <div className="mt-2 space-y-4">
          <div className="relative flex items-start">
            <div className="absolute flex h-6 items-center">
              <input
                id="permission-public"
                checked={permissions === PermissionType.Public}
                disabled={
                  !ability.can("Edit", "Input") ||
                  !ability.can("Submit", "Button")
                }
                onChange={() =>
                  handlePermissionUpdate(
                    PermissionType.Public,
                    Permissions.Public
                  )
                }
                value={PermissionType.Public}
                aria-describedby="permission-public-description"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <div
              className={classNames(
                !ability.can("Edit", "Input") ||
                  !ability.can("Submit", "Button")
                  ? "opacity-50"
                  : "",
                "pl-7 text-sm leading-6"
              )}>
              <label
                htmlFor="permission-public"
                className="font-medium text-gray-900">
                Public access
              </label>
              <p id="permission-public-description" className="text-gray-500">
                Everyone with the link will see this process.
              </p>
            </div>
          </div>
          <div className="relative flex items-start">
            <div className="absolute flex h-6 items-center">
              <input
                id="permission-group"
                checked={permissions === PermissionType.GroupShared}
                disabled={!ability.can("Edit", "Input")}
                onChange={() =>
                  handlePermissionUpdate(
                    PermissionType.GroupShared,
                    Permissions.GroupShared
                  )
                }
                value={PermissionType.GroupShared}
                aria-describedby="permission-group-description"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <div
              className={classNames(
                !ability.can("Edit", "Input") ? "opacity-50" : "",
                "pl-7 text-sm leading-6"
              )}>
              <label
                htmlFor="permission-group"
                className="font-medium text-gray-900">
                Private to group members
              </label>
              <p id="permission-group-description" className="text-gray-500">
                Only members of this process would be able to access.
              </p>
            </div>
          </div>
          {permissions === PermissionType.GroupShared && (
            <UserGroupSelectComponent
              name="groupId"
              isDisabled={!ability.can("Edit", "Input")}
              menuPlacement="top"
              missingId={[selectedGroup]}
              onChange={handleGroupIdUpdate}
            />
          )}
          <div>
            <div className="relative flex items-start">
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-private"
                  checked={permissions === PermissionType.UserOwned}
                  disabled={!canEditProcess}
                  value={PermissionType.UserOwned}
                  onChange={() =>
                    handlePermissionUpdate(
                      PermissionType.UserOwned,
                      Permissions.UserOwned
                    )
                  }
                  aria-describedby="permission-private-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div
                className={classNames(
                  !ability.can("Edit", "Input") ? "opacity-50" : "",
                  "pl-7 text-sm leading-6"
                )}>
                <label
                  htmlFor="permission-private"
                  className="font-medium text-gray-900">
                  Private to you
                </label>
                <p
                  id="permission-private-description"
                  className="text-gray-500">
                  You are the only one able to access this process.
                </p>
              </div>
            </div>
          </div>
        </div>
        <hr className="my-4" />
        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Group Permission to Write
        </label>
        <div className="mt-2 space-y-4">
          <UserGroupSelectComponent
            name="writeGroupId"
            isDisabled={!ability.can("Edit", "Input")}
            isMulti={true}
            missingId={selectedWriteUserGroups}
            onChange={handleWriteGroupIdUpdate}
          />
        </div>
      </fieldset>
    </div>
  );
}
